import { S, announce } from './state';
import * as A from './actions';
import { escapeHtml } from '../lib/format';
import { PIPELINE_COLOR, PIPELINE_DISPLAY } from '../lib/docs';
import { initTopbarFit } from '../features/topbar/topbarFit';
import { stagePointer } from '../features/map/stagePointer';
import type { SearchScope } from '../lib/types';

/**
 * THE PAGE CHROME — the parts of the document no region owns.
 *
 * The sidebar shell, the stage, the hint line, the zoom buttons, the AI-doc toolbar and the
 * icon defs are plain markup in `index.html`, exactly as they were in `infostation.html`. They
 * are not React because nothing about them is stateful in a way React would help with: the
 * sidebar's width is a transition on one element, the cursor is two classes on `#stage`, and
 * the hint is a string. Re-expressing them as components would have been a second change
 * riding along with the flip, with pixel baselines as the thing at risk.
 *
 * WHAT IS HERE IS THEREFORE IMPERATIVE ON PURPOSE, and it is the only imperative DOM left in
 * the app. Everything it touches is outside every React root, so there is no question of two
 * writers.
 *
 * `initChrome()` rather than work at module scope: this module and `actions.ts` refer to each
 * other, and doing anything at evaluation time would make that cycle load-bearing. Boot calls
 * it once, after the modules have settled and before anything is drawn.
 */

/* ------------------------------------------------------------------------- the hint */

const HINT_VIEW =
  '<b>View only</b> · click a node to see its description and tags · drag to pan · scroll to zoom';
const HINT_EDIT =
  '<b>Edit</b> · drag a node (children follow) · drop it onto another node to reparent · double-click for its menu · hold <b>Ctrl</b> and click to add · use <b>Link</b> to connect';
const HINT_LINK =
  '<b>Link</b> · click two nodes to join them with a dotted line · click Link again to cancel';

/** A "Move to…" from the right-click menu is waiting for its new parent (V0.54). */
const hintMove = (label: string) =>
  `<b>Move</b> · click the new parent for “${escapeHtml(label)}” · click empty map or press Esc to put it down`;
/** Something is on the clipboard (V0.54) — said while editing, where Paste is. */
const hintClip = (label: string, count: number) =>
  `<b>Copied</b> · “${escapeHtml(label)}”${count > 1 ? ` + ${count - 1}` : ''} · right-click a node or the map to paste, or press <b>Ctrl+V</b> · Esc empties it`;

/** What the hint currently says — so `updateHint()` can tell a change of wording from a repeat. */
let hintShown = '';

/**
 * The hint says what a click means in the current mode — and (V0.53.4) it SHOWS ITSELF WHEN
 * WHAT IT SAYS CHANGES: at boot, on a view <-> edit switch, when the Link tool is armed or
 * dropped. A call that would repeat the same wording — the Ctrl key in edit mode goes through
 * here on every press and release — changes nothing, including whether the hint is visible.
 * That is the rule that makes `dismissHint()` below hold: a hint the reader has panned away
 * stays away until the app has something new to say.
 */
export function updateHint(): void {
  const el = document.getElementById('hint');
  if (!el) return;
  const moving = S.movingId ? S.nodes[S.movingId] : null;
  const html = !S.editing
    ? HINT_VIEW
    : moving
      ? hintMove(moving.label)
      : S.linkArmed
        ? HINT_LINK
        : S.clipboard
          ? hintClip(S.clipboard.label, S.clipboard.count)
          : HINT_EDIT;
  if (html === hintShown) return;
  hintShown = html;
  el.innerHTML = html;
  el.classList.remove('gone');
}

/**
 * The hint steps aside (V0.53.4): called at the first movement of a pan — the map dragged, or
 * the minimap's frame — and nowhere else. Somebody who has just panned has read "drag to pan"
 * and no longer needs a line over the bottom of the map telling them so; a wheel notch, the
 * zoom buttons and a Ctrl press are not pans and leave it where it is. It comes back the next
 * time `updateHint()` has something different to say. Session state, by design: it is a class
 * on one element, and a reload shows the hint again as every session always has.
 */
export function dismissHint(): void {
  const el = document.getElementById('hint');
  if (el && !el.classList.contains('gone')) el.classList.add('gone');
}

/** What a click on the map MEANS, said in two classes on the stage. */
export function refreshCursor(): void {
  const stage = document.getElementById('stage');
  if (!stage) return;
  stage.classList.toggle('addmode', S.editing && S.ctrlAdd);
  stage.classList.toggle('linkmode', S.editing && S.linkArmed);
  stage.classList.toggle('movemode', S.editing && !!S.movingId);
  announce(); // ctrlAdd decides what a click on the map means, and the map region reads it
}

/* ---------------------------------------------------------------------- the sidebar */

export function applySidebar(): void {
  const sb = document.getElementById('sidebar');
  const tog = document.getElementById('side-toggle');
  const stage = document.getElementById('stage');
  if (!sb || !tog || !stage) return;
  sb.style.width = S.sidebarWidth + 'px';
  sb.classList.toggle('closed', !S.sidebarOpen);
  sb.style.transform = S.sidebarOpen ? 'translateX(0)' : `translateX(-${S.sidebarWidth}px)`;
  tog.classList.toggle('closed', !S.sidebarOpen);
  tog.style.left = S.sidebarOpen ? S.sidebarWidth + 10 + 'px' : '12px';
  stage.style.left = S.sidebarOpen ? S.sidebarWidth + 'px' : '0px';
  // The same value as a CSS variable, so anything anchored to the stage's left edge (the
  // heatmap legend, V0.39) positions in the stylesheet instead of a second element lookup.
  document.documentElement.style.setProperty('--stage-left', stage.style.left);
  // Keep the AI-doc toolbar attached just right of the sidebar's collapse toggle, so it never
  // hides behind the open sidebar and slides with it.
  //
  // Computed from the toggle's TARGET left (the same formula as above) — not `tog.offsetLeft`,
  // which returns the mid-flight value during the .22s transition and would strand the toolbar
  // under the panel on reopen. Width does not transition, so `offsetWidth` is safe to read.
  const bar = document.getElementById('aidoc-toolbar');
  if (bar) bar.style.left = (S.sidebarOpen ? S.sidebarWidth + 10 : 12) + tog.offsetWidth + 10 + 'px';
}

/* ----------------------------------------------------------------------- the top bar */

// The top bar's fit — measuring the zones and deciding what still fits the bar — lives in
// `features/topbar/topbarFit.ts` now (plan 1.3). The old `fitTopbar()` here could only hide
// the search; the controller moves controls into per-side "…" menus instead, and hides the
// centre only as a last resort. `initChrome()` below installs its ResizeObserver, which is
// what replaced this file's window `resize` listener.

/* -------------------------------------------------------------- the AI-doc toolbar */

/** The coloured dot beside the doc-status filter. */
export function syncAiDocFilterDot(): void {
  const f = document.getElementById('aidoc-status-field');
  const sel = document.getElementById('aidoc-status-filter') as HTMLSelectElement | null;
  if (sel && sel.value !== S.activeDocStatus) sel.value = S.activeDocStatus;
  if (f) {
    f.style.setProperty(
      '--sc',
      S.activeDocStatus ? PIPELINE_COLOR[S.activeDocStatus] || 'var(--status-not-started)' : 'transparent',
    );
  }
}

/**
 * Targets and the live-verification agent are their own capability.
 *
 * An offline build ships without the browser the agent drives, so the control is REMOVED
 * rather than left to fail. The logs panel's own Targets link applies the same gate itself;
 * what is left here is the toolbar button.
 */
export function applyLiveVerifyCapability(): void {
  const el = document.getElementById('btn-aidoc-targets');
  if (el) (el as HTMLElement).style.display = A.canLiveVerify() ? '' : 'none';
}

/* ------------------------------------------------------------- the language pickers */

/*
 * Nothing here any more (V0.53.5). Every picker is `components/LanguagePicker` — controlled
 * from the snapshot, one onChange — so there is nothing to keep in step and nothing to
 * delegate. Two mechanisms used to live here: a `syncLangPickers()` that wrote the current
 * locale into every `.lang-picker` for the two pickers that were uncontrolled, and a delegated
 * `change` listener that called `setLocale` for any picker but the top bar's — which, for the
 * three controlled pickers that also carried the class, meant `setLocale` twice per change,
 * saved only by its early return. Before those, the history below.
 *
 * There was a `mountLangPickers()` here until V0.35. It walked `.perm-head, .docbar` at boot
 * and injected a `<select class="lang-picker">` into any header that had not rendered one —
 * the vanilla app's mechanism, kept as a fallback because every region renders its own now,
 * and documented as finding nothing in practice.
 *
 * Deep links (V0.32) made "in practice" stop being true. A reload on `/{locale}/admin/…`
 * re-opens that screen DURING boot, so a `.perm-head` is in the DOM when the walk runs — and
 * at that moment it has no picker, because a picker needs `S.languages`, which the walk's own
 * caller had only just fetched, and React had not re-rendered yet. So it injected one, React
 * then rendered the region's own, and the header carried two. It would also have added a
 * picker to a doc bar that is designed not to have one, on a reload of a `/doc/{slug}` link.
 *
 * A fallback that only ever fires in the situation it cannot handle is not a fallback, so it
 * is gone rather than guarded: the regions own their pickers, and `syncLangPickers()` below
 * keeps them in step.
 */

/* -------------------------------------------------------------------------- wiring */

let wired = false;

/** Attach every listener the page itself owns. Idempotent; boot calls it once. */
export function initChrome(): void {
  if (wired) return;
  wired = true;

  /* ---- the sidebar ---- */
  const sideToggle = document.getElementById('side-toggle');
  if (sideToggle) {
    sideToggle.onclick = () => {
      S.sidebarOpen = !S.sidebarOpen;
      applySidebar();
      A.saveLocal(); // the sidebar is furniture: a durable pref, never map content
    };
  }
  const rez = document.getElementById('sidebar-resize');
  if (rez) {
    rez.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = S.sidebarWidth;
      document.body.classList.add('resizing-sidebar');
      try {
        rez.setPointerCapture(e.pointerId);
      } catch {
        /* not every pointer can be captured */
      }
      const move = (ev: PointerEvent) => {
        S.sidebarWidth = Math.max(200, Math.min(560, startW + (ev.clientX - startX)));
        applySidebar();
      };
      const up = () => {
        document.body.classList.remove('resizing-sidebar');
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        A.saveLocal(); // as above — the width is this browser’s
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    });
  }

  // The tag pane's footer and clear button used to be wired here while the pane was sidebar
  // markup. Plan 2.3 moved the whole pane into the topbar's tags dropdown, where React
  // renders and wires them (features/topbar/TopbarSearch.tsx).

  /* ---- the pipeline-stage filter (V0.38; lives in the AI-doc toolbar, so it only ever
     offers itself where the mode's own gate — editing && canAiAdmin — guarantees the
     per-node `pipelineStage` data exists, the same spirit as the old doc-status gating) ---- */
  const filter = document.getElementById('aidoc-status-filter') as HTMLSelectElement | null;
  if (filter) {
    filter.innerHTML =
      '<option value="">Any stage</option>' +
      PIPELINE_DISPLAY.map(
        (v) => `<option value="${v.key}" title="${escapeHtml(v.hint)}">${escapeHtml(v.label)}</option>`,
      ).join('');
    filter.addEventListener('change', () => A.setActiveDocStatus(filter.value));
  }

  /* ---- the AI-doc toolbar's buttons ---- */
  const on = (id: string, fn: () => void) => {
    const el = document.getElementById(id);
    if (el) el.onclick = fn;
  };
  on('btn-generate-docs', () => A.openAiDocModal());
  on('btn-aidoc-logs', () => A.openLogs());
  on('btn-aidoc-targets', () => A.openTargets());
  on('btn-aidoc-config', () => A.openAiDocConfig());

  /* ---- the zoom buttons ---- */
  // The documents toggle sits in its own card above them (V0.42.2): it pins the
  // documentation rows open at any zoom, instead of leaving it to the zoom threshold.
  on('btn-docs', () => A.toggleDocsPinned());
  // The minimap (V0.53.4): the first row of the stack, above Recenter.
  on('btn-minimap', () => A.toggleMinimap());
  on('btn-recenter', () => A.fit());
  on('zin', () => A.zoomBtn(1.2));
  on('zout', () => A.zoomBtn(0.8));

  /* ---- keyboard ---- */
  // Ctrl-hold = the add cursor. Only meaningful in edit mode WITH the map brick (creating a
  // node is an EDIT_MAP act — V0.37), and not while a popup has focus.
  window.addEventListener('keydown', (e) => {
    if ((e.key === 'Control' || e.key === 'Meta') && S.editing && A.canEditMap() && !S.linkArmed && !S.ctrlAdd && !S.popupFor) {
      S.ctrlAdd = true;
      refreshCursor();
      updateHint();
    }
    if (e.key === 'Escape') {
      // Not while a page is open over the popup (V0.47): the doc view's own Escape closes the
      // page and hands the node back to its seat; closing the popup underneath at the same
      // moment would take the seat away mid-flight. Two presses to the map, where it was one.
      if (S.popupFor && !S.docOpen) A.closePopup({ fly: true });
      else if (S.movingId) A.cancelMove();
      else if (S.linkArmed) A.armLink();
      else if (S.clipboard && !S.docOpen) A.clearClipboard();
    }
    clipboardKeys(e);
  });
  window.addEventListener('keyup', (e) => {
    if (e.key === 'Control' || e.key === 'Meta' || (!e.ctrlKey && !e.metaKey)) A.releaseCtrl();
  });
  window.addEventListener('blur', () => A.releaseCtrl());
  // Escape closes the AI context screen. The buttons inside it are React's; this is the keyboard
  // path, which belongs to the page rather than to any one region. (The generation console had a
  // line here too until V0.44.1 moved it into the dashboard, whose own Escape handler covers it.)
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (document.getElementById('aidoccfgmodal')?.classList.contains('show')) A.closeAiDocConfig();
  });

  // The top bar re-fits itself when its box changes — a ResizeObserver on the bar, installed
  // once, covering window resizes and anything else that moves the bar's width.
  initTopbarFit();
}

/**
 * Ctrl+C, Ctrl+V and Ctrl+D on the map (V0.54) — the right-click menu's Copy, Paste and
 * Duplicate, for the node under the pointer. (Cut was not chosen, so Ctrl+X means nothing
 * here.) Edit mode with the map brick only, as the menu items are, and never while focus is in
 * a text field — a field's own copy and paste are not ours to take. Ctrl+V lands on the node
 * under the pointer, or on the empty map where the pointer is; anywhere else it is left to the
 * browser. Shift+Ctrl+C copies the node with its branch.
 */
function clipboardKeys(e: KeyboardEvent): void {
  if (!(e.ctrlKey || e.metaKey) || e.altKey || !S.editing || !A.canEditMap()) return;
  const k = e.key.toLowerCase();
  if (k !== 'c' && k !== 'v' && k !== 'd') return;
  const t = e.target as HTMLElement | null;
  if (t?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
  if (S.popupFor || S.docOpen) return;
  const hovered = document.querySelector<HTMLElement>('#world .node:hover')?.dataset.id ?? null;
  if (k === 'c' || k === 'd') {
    if (!hovered || !S.nodes[hovered] || S.nodes[hovered].root) return;
    e.preventDefault(); // Ctrl+D would otherwise bookmark the page
    if (k === 'd') void A.duplicateNode(hovered);
    else A.copyNode(hovered, e.shiftKey && A.childrenOf(hovered).length > 0);
    return;
  }
  if (!S.clipboard) return;
  if (hovered && S.nodes[hovered]) {
    e.preventDefault();
    void A.pasteInto(hovered);
    return;
  }
  const p = stagePointer();
  if (!p) return;
  e.preventDefault();
  const w = A.screenToWorld(p.x, p.y);
  if (w) void A.pasteAt(w.x, w.y);
}

/** The durable prefs, applied to the chrome after they are read. */
export function applyPrefs(prefs: {
  isLight: boolean;
  sidebarOpen: boolean;
  sidebarWidth: number;
  editing: boolean;
  searchScope: SearchScope;
  minimapOn: boolean;
  minimapBright: boolean;
}): void {
  S.isLight = prefs.isLight;
  S.sidebarOpen = prefs.sidebarOpen;
  S.sidebarWidth = prefs.sidebarWidth;
  S.editing = prefs.editing;
  S.searchScope = prefs.searchScope;
  S.minimapOn = prefs.minimapOn;
  S.minimapBright = prefs.minimapBright;
  syncMinimapButton();
  // showLinks is deliberately NOT among them — it starts off every session (V0.48.1).
}

/**
 * The minimap button's pressed state (V0.53.4), from `S.minimapOn`.
 *
 * The button is markup in `index.html` and the card is a React region, so the two meet in the
 * state and nowhere else: boot writes the button from the stored preference here, and
 * `setMinimapOn()` writes it again on every toggle.
 */
export function syncMinimapButton(): void {
  const btn = document.getElementById('btn-minimap');
  if (!btn) return;
  btn.classList.toggle('on', S.minimapOn);
  btn.setAttribute('aria-pressed', S.minimapOn ? 'true' : 'false');
  btn.setAttribute('title', S.minimapOn ? 'Hide the minimap' : 'Show the minimap');
}
